import React, { useCallback, useState } from 'react';
import { View, Pressable, ScrollView, StyleSheet, Platform } from 'react-native';
import { Text } from '@/components/common/text';
import Icon from 'react-native-vector-icons/Ionicons';
import { useAppDispatch, useAppSelector } from '@/store';
import { setActiveContext } from '@/store/slices/authSlice';
import { authApi } from '@/store/api/authApi';
import type { AvailableContext } from '@/types/auth';
import { useThemedStyles, useThemeColors } from '@/hooks/shared/useThemeColors';
import type { ThemeContextValue } from '@/hooks/shared/useThemeColors';
import { useResponsive } from '@/hooks/common/useResponsive';
import { useReducedMotion } from '@/hooks/shared/useReducedMotion';
import { buildCardHoverStyle } from '@/components/shared/redesign/buildCardHoverStyle';
import { LoginHeroHeader } from '@/components/LoginHeroHeader';
import { TYPOGRAPHY } from '@/constants/shared/theme/typography';
import { RADIUS } from '@/constants/shared/theme/radius';
import { SPACING } from '@/constants/shared/theme/spacing';

/**
 * Unified Auth — logged-in context picker (design §5.2, RES-89).
 *
 * Shown by `RootNavigator` after login (and on relaunch) when the account has
 * more than one available context. Greenfield screen — the pre-signup
 * `UserSelectionScreen` is a separate resident-vs-guest chooser, left untouched.
 *
 * One compact card, two arrangements. The card is a fixed width on desktop and
 * laid out in a centered, wrapping row — every card the same size regardless of
 * how many contexts the account has, never flex-growing to fill. On a phone the
 * same card goes full-width and stacks. So a card's width never shifts with the
 * number of contexts, and desktop/mobile are the same object in two layouts,
 * chosen by the app's existing `useResponsive` breakpoints. Brand blue marks an
 * admin context, resident-green a tenancy, from the theme's accent tokens.
 *
 * On web the card lifts on hover (shared `buildCardHoverStyle`, suppressed under
 * reduced motion); on native it is a plain press target.
 *
 * Tapping a card selects the context and flushes the RTK Query caches so the
 * previously-active context's data can't leak into the newly-chosen one
 * (design R8). `activeContext` is not persisted, so a fresh launch re-shows this.
 */
export const CONTEXT_PICKER_TITLE = 'Choose an account';
const CONTEXT_PICKER_SUBTITLE = "You're signed in — pick where you'd like to go.";

// A fixed card width on desktop keeps every card identical whatever the context
// count; the row wraps to a tidy second line rather than stretching. Not a
// tokenised value because it is a layout dimension, not spacing/type/radius.
const DESKTOP_CARD_WIDTH = 262;

const titleCase = (value: string): string =>
  value ? value.charAt(0).toUpperCase() + value.slice(1) : value;

// One Ionicon + label per kind. The accent color is resolved from the live
// theme inside the card so admin/resident stay distinguishable in light and dark.
const KIND: Record<AvailableContext['kind'], { icon: string; label: string }> = {
  admin: { icon: 'briefcase', label: 'Admin' },
  resident: { icon: 'home', label: 'Resident' },
};

const ContextCard: React.FC<{
  context: AvailableContext;
  onSelect: (context: AvailableContext) => void;
  isDesktop: boolean;
  styles: ReturnType<typeof makeStyles>;
}> = ({ context, onSelect, isDesktop, styles }) => {
  const { colors, semantic } = useThemeColors();
  const reduceMotion = useReducedMotion();
  const [hovered, setHovered] = useState(false);
  const isWeb = Platform.OS === 'web';

  const isAdmin = context.kind === 'admin';
  const meta = KIND[context.kind];

  // Brand blue for admin, resident green for resident — from the theme's accent
  // system, so the two kinds are scannable without shouting.
  const accentFg = isAdmin ? semantic.interactive.primary : semantic.status.success;
  const accentBg = isAdmin ? colors.accent.blueBg : colors.accent.greenBg;

  // Primary line is the thing you're choosing (the org, or the property you rent);
  // the rest is supporting detail.
  const primary = isAdmin ? context.orgName : context.propertyName;
  const details = isAdmin
    ? [context.propertyName, context.roleName]
    : [context.leaseDisplayAddress, `Lease · ${titleCase(context.leaseStatus)}`];

  // Web-only hover lift (shadow + translate); null on native / reduced motion.
  const hoverLift = buildCardHoverStyle({
    isHovered: hovered,
    reduceMotion,
    isWeb,
    shadowHover: colors.card.shadowHover,
    liftPx: 4,
  });

  return (
    <Pressable
      testID={`context-row-${context.contextId}`}
      accessibilityRole="button"
      accessibilityLabel={`${meta.label} — ${[primary, ...details].filter(Boolean).join(', ')}`}
      onPress={() => onSelect(context)}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      style={[
        styles.card,
        isDesktop ? styles.cardDesktop : styles.cardMobile,
        hovered && { borderColor: colors.card.borderHover },
        hoverLift,
      ]}
    >
      <View style={styles.head}>
        <View style={[styles.avatar, { backgroundColor: accentBg }]}>
          <Icon name={meta.icon} size={21} color={accentFg} />
        </View>
        <Text style={[styles.kind, { color: accentFg }]}>{meta.label}</Text>
        <Icon
          name="chevron-forward"
          size={20}
          color={hovered ? accentFg : semantic.icon.muted}
        />
      </View>

      {primary ? (
        <Text style={styles.name} numberOfLines={2}>
          {primary}
        </Text>
      ) : null}
      {details
        .filter((line): line is string => Boolean(line && line.length > 0))
        .map((line, index) => (
          <Text key={index} style={styles.detail}>
            {line}
          </Text>
        ))}
    </Pressable>
  );
};

export const ContextPickerScreen: React.FC = () => {
  const dispatch = useAppDispatch();
  const styles = useThemedStyles(makeStyles);
  const { isDesktop } = useResponsive();
  const availableContexts = useAppSelector((state) => state.auth.availableContexts);

  const handleSelect = useCallback(
    (context: AvailableContext) => {
      dispatch(setActiveContext(context));
      // Flush every cached query/mutation so the other context's data can't leak.
      dispatch(authApi.util.resetApiState());
    },
    [dispatch],
  );

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      <LoginHeroHeader title={CONTEXT_PICKER_TITLE} subtitle={CONTEXT_PICKER_SUBTITLE} />
      <View style={[styles.cards, isDesktop ? styles.cardsDesktop : styles.cardsMobile]}>
        {availableContexts.map((context) => (
          <ContextCard
            key={context.contextId}
            context={context}
            onSelect={handleSelect}
            isDesktop={isDesktop}
            styles={styles}
          />
        ))}
      </View>
    </ScrollView>
  );
};

const makeStyles = ({ colors, semantic }: ThemeContextValue) =>
  StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: colors.bg.base,
    },
    content: {
      flexGrow: 1,
      paddingBottom: SPACING['3xl'],
    },
    cards: {
      width: '100%',
      alignSelf: 'center',
      paddingHorizontal: SPACING.xl,
      paddingTop: SPACING['2xl'],
    },
    // Desktop: a centered, wrapping row of fixed-width cards.
    cardsDesktop: {
      maxWidth: 900,
      flexDirection: 'row',
      flexWrap: 'wrap',
      justifyContent: 'center',
      alignItems: 'stretch',
      gap: SPACING.lg,
    },
    // Mobile: a single centered column of full-width cards.
    cardsMobile: {
      maxWidth: 480,
      gap: SPACING.md,
    },
    card: {
      backgroundColor: colors.card.bg,
      borderRadius: RADIUS.lg,
      borderWidth: StyleSheet.hairlineWidth,
      borderColor: colors.card.border,
      padding: SPACING.lg,
      // Soft lift so each card reads as a tappable surface, not a flat block.
      shadowColor: semantic.shadow.card,
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 1,
      shadowRadius: 8,
      elevation: 2,
    },
    cardDesktop: {
      width: DESKTOP_CARD_WIDTH,
    },
    cardMobile: {
      width: '100%',
    },
    head: {
      flexDirection: 'row',
      alignItems: 'center',
      marginBottom: SPACING.md,
    },
    avatar: {
      width: 44,
      height: 44,
      borderRadius: RADIUS.full,
      justifyContent: 'center',
      alignItems: 'center',
      marginRight: SPACING.md,
    },
    kind: {
      ...TYPOGRAPHY.label,
      flex: 1,
    },
    name: {
      ...TYPOGRAPHY.cardTitleLarge,
      color: colors.text.primary,
      marginBottom: SPACING.xs,
    },
    detail: {
      ...TYPOGRAPHY.caption,
      color: colors.text.muted,
      lineHeight: 18,
    },
  });

export default ContextPickerScreen;
